<script setup lang="ts">
type Emits = {
  close: [];
};

const props = defineProps<{
  zIndex?: number;
  borderColor?: string;
  bgColor?: string;
  top?: string;
  /** Libera overflow do wrapper/inner para que efeitos (drop-shadow, halos)
   *  dos filhos possam transbordar — útil pra modais com aura/glow visual.
   *  Não use se o conteúdo precisar rolar. */
  allowOverflow?: boolean;
}>();

const emit = defineEmits<Emits>();

const modalStyles = computed(() => {
  const styles: Record<string, string> = {};

  if (props.borderColor) {
    styles['--modal-border-color'] = props.borderColor;
  }

  if (props.bgColor) {
    styles['--modal-bg-color'] = props.bgColor;
  }

  return styles;
});

function closeDialog() {
  isVisible.value = false;
  emit('close');
}

const isVisible = defineModel<boolean>('modelValue', {
  required: false,
  default: false,
});

const { header, bottomMenu } = storeToRefs(useLayoutStore());
const headerHeight = computed(() => header.value.height);
const bottomMenuHeight = computed(() => bottomMenu.value.height || 0);

// Store original overflow to restore it properly
let originalOverflow = '';

// Prevent body scroll when modal is open
watch(isVisible, (newVal) => {
  if (import.meta.client) {
    if (newVal) {
      originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      document.body.classList.add('modal-open');
    } else {
      document.body.style.overflow = originalOverflow;
      document.body.classList.remove('modal-open');
    }
  }
});

// Cleanup on unmount to prevent memory leaks
onUnmounted(() => {
  if (import.meta.client && isVisible.value) {
    document.body.style.overflow = originalOverflow;
    document.body.classList.remove('modal-open');
  }
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="isVisible"
      :class="$style.modal"
      :style="{
        top: props.top || headerHeight + 'px',
        '--header-height': headerHeight + 'px',
        '--bottom-menu-height': bottomMenuHeight + 'px',
        zIndex: props.zIndex || 19,
        ...modalStyles,
      }"
      @click.self.stop="closeDialog"
    >
      <div
        :class="[$style.wrapper, props.allowOverflow && $style.allowOverflow]"
      >
        <div :class="$style.header">
          <slot name="header" />
          <button
            :class="$style.closeButton"
            @click="closeDialog"
          >
            <NuxtIcon
              class="font-bold text-[16px]"
              name="close"
            />
          </button>
        </div>
        <div :class="$style.inner">
          <slot />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style module lang="postcss">
.modal {
  @apply fixed inset-0 bg-[#030C27c5] z-[19] flex justify-center items-center lg:items-center select-none backdrop-blur-sm lg:pb-0;
  /* Usa o maior valor entre bottomMenu.height e safe-area-inset-bottom */
  padding-bottom: max(
    var(--bottom-menu-height, 0px),
    env(safe-area-inset-bottom, 0px)
  );

  .wrapper {
    @apply relative flex pb-2 flex-col mx-auto transform text-left align-middle shadow-xl w-[352px] rounded-[10px] mt-[20px] lg:mt-0;
    border: 1.038px solid var(--modal-border-color, #1d2329);
    background: var(--modal-bg-color, theme('colors.bg-primary'));
    /* Usa 100dvh para considerar viewport dinâmico em mobile */
    max-height: calc(100dvh - var(--header-height) - 100px);
  }

  .header {
    @apply flex items-start justify-between px-5 py-4 border-b border-white/70;
  }

  .closeButton {
    @apply w-[32px] h-[32px] text-white/70 hover:text-white transition-colors flex justify-center items-center;

    svg {
      @apply w-[1.5em] h-[1.5em];
    }
  }

  .inner {
    @apply overflow-y-auto px-5;
  }

  .wrapper.allowOverflow,
  .wrapper.allowOverflow .inner {
    overflow: visible;
  }

  .footer {
    @apply px-5 pt-4 pb-5 border-t border-white/70;
  }

  .footerCloseButton {
    @apply relative w-full h-8 rounded-md border border-solid border-white/50 bg-white/10 opacity-100 px-[10px] flex gap-[7px] cursor-pointer hover:brightness-75;
  }
}
</style>
